const STORAGE_KEY = "fastrecovery.notificationSound";
const SOUND_MS = 520;

export function readNotificationSoundEnabled(storage) {
  try {
    const value = storage ? storage.getItem(STORAGE_KEY) : null;
    if (value == null || value === "") return true;
    return value === "on";
  } catch {
    return true;
  }
}

export function writeNotificationSoundEnabled(enabled, storage) {
  if (!storage) return Boolean(enabled);
  storage.setItem(STORAGE_KEY, enabled ? "on" : "off");
  return Boolean(enabled);
}

export function decideNotificationSound({ show, enabled, unlocked, playing } = {}) {
  if (!show) return { play: false, reason: "not-new" };
  if (!enabled) return { play: false, reason: "muted" };
  if (!unlocked) return { play: false, reason: "blocked" };
  if (playing) return { play: false, reason: "overlap" };
  return { play: true, reason: "play" };
}

export function scheduleDingDong(ctx, when = 0) {
  [523.25, 659.25].forEach((frequency, index) => {
    const start = when + index * 0.16;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.05, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(start + 0.24);
  });
}

export function createNotificationSoundController({ storage, play, onBlocked } = {}) {
  let unlocked = false;
  let blocked = false;
  let lastPlayedAt = null;

  function emitBlocked() {
    if (blocked) return;
    blocked = true;
    if (typeof onBlocked === "function") onBlocked();
  }

  return {
    isBlocked: () => blocked,
    unlock() {
      unlocked = true;
      blocked = false;
    },
    setEnabled(enabled) {
      return writeNotificationSoundEnabled(enabled, storage);
    },
    play(show, now = 0) {
      const decision = decideNotificationSound({
        show: Boolean(show),
        enabled: readNotificationSoundEnabled(storage),
        unlocked,
        playing: lastPlayedAt != null && now - lastPlayedAt < SOUND_MS,
      });
      if (!decision.play) {
        if (decision.reason === "blocked") emitBlocked();
        return decision;
      }
      try {
        if (typeof play === "function") play();
        lastPlayedAt = now;
        blocked = false;
        return decision;
      } catch {
        emitBlocked();
        return { play: false, reason: "blocked" };
      }
    },
  };
}

let sharedContext = null;
let audio = null;
let controller = null;
let armInstalled = false;

function browserStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function notifySoundState(detail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("app:notification-sound", { detail }));
}

function writeString(view, offset, value) {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

export function buildDingWavUrl() {
  const sampleRate = 22050;
  const samples = Math.floor(sampleRate * 0.42);
  const buffer = new ArrayBuffer(44 + samples * 2);
  const view = new DataView(buffer);
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + samples * 2, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, samples * 2, true);
  for (let index = 0; index < samples; index += 1) {
    const time = index / sampleRate;
    const ding = time < 0.2 ? Math.sin(2 * Math.PI * 880 * time) * Math.exp(-time * 8) : 0;
    const dong = time > 0.15 ? Math.sin(2 * Math.PI * 1174.66 * (time - 0.15)) * Math.exp(-(time - 0.15) * 7) : 0;
    const sample = Math.max(-1, Math.min(1, (ding + dong) * 0.9));
    view.setInt16(44 + index * 2, sample * 32767, true);
  }
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
  return `data:audio/wav;base64,${btoa(binary)}`;
}

function ensureAudio() {
  if (audio) return audio;
  audio = new Audio(buildDingWavUrl());
  audio.preload = "auto";
  return audio;
}

function getController() {
  if (!controller) {
    controller = createNotificationSoundController({
      storage: browserStorage(),
      play() {
        if (!audio) throw new Error("audio-blocked");
        audio.volume = 1;
        try {
          audio.currentTime = 0;
        } catch {
          /* The clip can restart once the browser has buffered it. */
        }
        const attempt = audio.play();
        if (attempt && typeof attempt.catch === "function") attempt.catch(() => {});
      },
      onBlocked() {
        notifySoundState({ blocked: true });
      },
    });
  }
  return controller;
}

export function playNotificationAlert() {
  return getController().play(true, Date.now());
}

export function enableNotificationSoundFromGesture({ preview = true } = {}) {
  getController().setEnabled(true);
  if (typeof Audio === "undefined") {
    notifySoundState({ enabled: true, blocked: true });
    return false;
  }
  try {
    const clip = ensureAudio();
    clip.volume = preview ? 1 : 0;
    try {
      clip.currentTime = 0;
    } catch {
      /* Play from the start after the clip loads. */
    }
    const attempt = clip.play();
    getController().unlock();
    if (!preview) clip.volume = 1;
    notifySoundState({ enabled: true, blocked: false, unlocked: true });
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => {
        if (preview) notifySoundState({ enabled: true, blocked: true });
      });
    }
    return true;
  } catch {
    notifySoundState({ enabled: true, blocked: true });
    return false;
  }
}

export function armNotificationSoundUnlock() {
  if (armInstalled || typeof window === "undefined") return;
  armInstalled = true;
  window.addEventListener(
    "pointerdown",
    () => {
      if (!readNotificationSoundEnabled(browserStorage())) return;
      enableNotificationSoundFromGesture({ preview: false });
    },
    { capture: true, once: true }
  );
}

export function disableNotificationSound() {
  getController().setEnabled(false);
  notifySoundState({ enabled: false, blocked: false });
}
